import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Cache the measurement ID to avoid repeated failed requests
let cachedMeasurementId: string | null = null;
let fetchAttempted = false;

export const useDynamicGA4 = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const measurementIdRef = useRef<string | null>(null);

  useEffect(() => {
    const loadGA4 = async () => {
      try {
        // Use cached value if available
        if (cachedMeasurementId) {
          measurementIdRef.current = cachedMeasurementId;
          initializeGA4(cachedMeasurementId);
          return;
        }

        // Only attempt fetch once per session
        if (fetchAttempted) return;
        fetchAttempted = true;

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        // Fetch measurement ID from edge function (non-blocking)
        const { data, error } = await supabase.functions.invoke('get-ga4-config', {
          // Note: supabase-js doesn't support signal directly, but timeout prevents blocking
        });
        
        clearTimeout(timeoutId);
        
        if (error || !data?.measurementId) {
          // Silently fail - GA4 is not critical for page functionality
          console.log('GA4 not configured or unavailable');
          return;
        }

        const measurementId = data.measurementId;
        cachedMeasurementId = measurementId;
        measurementIdRef.current = measurementId;
        
        initializeGA4(measurementId);
      } catch (error) {
        // Silently fail - GA4 should never block page load
        console.log('GA4 load skipped');
      }
    };

    const initializeGA4 = (measurementId: string) => {
      // Check if already loaded
      if (document.querySelector(`script[src*="${measurementId}"]`)) {
        setIsLoaded(true);
        return;
      }

      // Create script element for GA4 (async, non-blocking)
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        window.dataLayer.push(args);
      }
      window.gtag = gtag;

      gtag('js', new Date());
      gtag('config', measurementId, {
        page_title: document.title,
        page_location: window.location.href,
      });

      setIsLoaded(true);
    };

    // Load GA4 without blocking - use setTimeout to defer
    setTimeout(loadGA4, 0);
  }, []);

  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (window.gtag && isLoaded) {
      window.gtag('event', eventName, parameters);
    }
  };

  const trackPageView = (pagePath: string, pageTitle?: string) => {
    if (window.gtag && isLoaded && measurementIdRef.current) {
      window.gtag('config', measurementIdRef.current, {
        page_path: pagePath,
        page_title: pageTitle || document.title,
      });
    }
  };

  return { trackEvent, trackPageView, isLoaded };
};